import React, { useState } from 'react';
import AppHeader from '../../../components/AppHeader.tsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowDown, faArrowUp, faCalendar, faChartPie, faCut, faDownload, faEuroSign, faEye, faMinus, faPercentage, faUsers } from '@fortawesome/free-solid-svg-icons';

const RevenueDistributionPage: React.FC = () => {
  const [selectedPeriod, setSelectedPeriod] = useState('month');
  const [viewMode, setViewMode] = useState('services');

  const breadcrumb = [{ label: 'Financování' }, { label: 'Rozdělení výnosů' }];

  const revenueDistribution = {
    totalRevenue: 156230,
    services: [
      { name: 'Střih a foukaná', revenue: 78400, percentage: 50.2, color: '#3B82F6' },
      { name: 'Barvení vlasů', revenue: 45000, percentage: 28.8, color: '#8B5CF6' },
      { name: 'Kosmetické ošetření', revenue: 18000, percentage: 11.5, color: '#EC4899' },
      { name: 'Manikúra', revenue: 11400, percentage: 7.3, color: '#EF4444' },
      { name: 'Holení', revenue: 3430, percentage: 2.2, color: '#10B981' },
    ],
    staff: [
      { name: 'Anna Svobodová', revenue: 39200, percentage: 25.1, services: 98, color: '#3B82F6' },
      { name: 'Petr Novák', revenue: 34800, percentage: 22.3, services: 87, color: '#8B5CF6' },
      { name: 'Marie Krásná', revenue: 45600, percentage: 29.2, services: 76, color: '#EC4899' },
      { name: 'Jana Nová', revenue: 36630, percentage: 23.4, services: 81, color: '#EF4444' },
    ],
    timeSlots: [
      { time: '08:00-10:00', revenue: 12400, percentage: 7.9, color: '#6B7280' },
      { time: '10:00-12:00', revenue: 23400, percentage: 15.0, color: '#3B82F6' },
      { time: '12:00-14:00', revenue: 19800, percentage: 12.7, color: '#8B5CF6' },
      { time: '14:00-16:00', revenue: 28700, percentage: 18.4, color: '#EC4899' },
      { time: '16:00-18:00', revenue: 38200, percentage: 24.4, color: '#EF4444' },
      { time: '18:00-20:00', revenue: 33730, percentage: 21.6, color: '#10B981' },
    ],
  };

  const comparisonData = {
    previousPeriod: {
      totalRevenue: 138450,
      services: [
        { name: 'Střih a foukaná', revenue: 69400, percentage: 50.1 },
        { name: 'Barvení vlasů', revenue: 41500, percentage: 30.0 },
        { name: 'Kosmetické ošetření', revenue: 13800, percentage: 10.0 },
        { name: 'Manikúra', revenue: 9700, percentage: 7.0 },
        { name: 'Holení', revenue: 4050, percentage: 2.9 },
      ],
    },
    growth: {
      total: 12.8,
      services: [
        { name: 'Střih a foukaná', growth: 13.0 },
        { name: 'Barvení vlasů', growth: 8.4 },
        { name: 'Kosmetické ošetření', growth: 30.4 },
        { name: 'Manikúra', growth: 17.5 },
        { name: 'Holení', growth: -15.3 },
      ],
    },
  };

  const getTrendIcon = (value: number) => {
    if (value > 0) return faArrowUp;
    if (value < 0) return faArrowDown;
    return faMinus;
  };

  const getTrendColor = (value: number) => {
    if (value > 0) return 'text-green-600';
    if (value < 0) return 'text-red-600';
    return 'text-gray-600';
  };

  const renderServiceDistribution = () => (
    <div className="space-y-6">
      {/* Services Chart */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Rozdělení podle služeb</h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Pie Chart Placeholder */}
          <div className="flex items-center justify-center">
            <div className="w-64 h-64 bg-gray-100 rounded-full flex items-center justify-center">
              <div className="text-center">
                <FontAwesomeIcon icon={faChartPie} className="w-16 h-16 text-gray-400 mb-2" />
                <p className="text-gray-600">Graf výnosů</p>
              </div>
            </div>
          </div>

          {/* Services List */}
          <div className="space-y-4">
            {revenueDistribution.services.map((service, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className="w-4 h-4 rounded-full" style={{ backgroundColor: service.color }}></div>
                  <div>
                    <p className="font-medium text-gray-900">{service.name}</p>
                    <p className="text-sm text-gray-600">{service.percentage}% z celku</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-bold text-gray-900">{service.revenue.toLocaleString()} Kč</p>
                  <div className="flex items-center text-sm">
                    <FontAwesomeIcon
                      icon={getTrendIcon(comparisonData.growth.services[index].growth)}
                      className={`mr-1 ${getTrendColor(comparisonData.growth.services[index].growth)}`}
                    />
                    <span className={getTrendColor(comparisonData.growth.services[index].growth)}>
                      {comparisonData.growth.services[index].growth > 0 ? '+' : ''}
                      {comparisonData.growth.services[index].growth}%
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Services Comparison */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Srovnání s minulým obdobím</h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Služba</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tento měsíc</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Minulý měsíc</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Změna</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Trend</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {revenueDistribution.services.map((service, index) => {
                const previousRevenue = comparisonData.previousPeriod.services[index].revenue;
                const growth = comparisonData.growth.services[index].growth;

                return (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{service.name}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{service.revenue.toLocaleString()} Kč</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{previousRevenue.toLocaleString()} Kč</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{(service.revenue - previousRevenue).toLocaleString()} Kč</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <FontAwesomeIcon icon={getTrendIcon(growth)} className={`w-4 h-4 mr-1 ${getTrendColor(growth)}`} />
                        <span className={`text-sm ${getTrendColor(growth)}`}>
                          {growth > 0 ? '+' : ''}
                          {growth}%
                        </span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const renderStaffDistribution = () => (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Rozdělení podle zaměstnanců</h3>
        <div className="space-y-4">
          {revenueDistribution.staff.map((staff, index) => (
            <div key={index} className="p-4 border border-gray-200 rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                    <FontAwesomeIcon icon={faUsers} className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900">{staff.name}</h4>
                    <p className="text-sm text-gray-600">{staff.services} služeb</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-lg font-bold text-gray-900">{staff.revenue.toLocaleString()} Kč</p>
                  <p className="text-sm text-gray-600">{staff.percentage}% z celku</p>
                </div>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div
                  className="h-3 rounded-full transition-all duration-300"
                  style={{
                    width: `${staff.percentage}%`,
                    backgroundColor: staff.color,
                  }}
                ></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderTimeDistribution = () => (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Rozdělení podle časových slotů</h3>
        <div className="space-y-4">
          {revenueDistribution.timeSlots.map((slot, index) => (
            <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center space-x-3">
                <div className="w-4 h-4 rounded-full" style={{ backgroundColor: slot.color }}></div>
                <div>
                  <p className="font-medium text-gray-900">{slot.time}</p>
                  <p className="text-sm text-gray-600">{slot.percentage}% z celku</p>
                </div>
              </div>
              <div className="text-right">
                <p className="font-bold text-gray-900">{slot.revenue.toLocaleString()} Kč</p>
                <p className="text-sm text-gray-600">Průměr: {Math.round(slot.revenue / 2).toLocaleString()} Kč/hod</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderSelectedView = () => {
    switch (viewMode) {
      case 'services':
        return renderServiceDistribution();
      case 'staff':
        return renderStaffDistribution();
      case 'time':
        return renderTimeDistribution();
      default:
        return renderServiceDistribution();
    }
  };

  return (
    <div className="h-full flex flex-col">
      <AppHeader title="Rozdělení výnosů" breadcrumb={breadcrumb} />

      <div className="flex-1 overflow-y-auto bg-gray-50">
        <div className="p-6 space-y-6">
          {/* Header */}
          <section className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-semibold text-gray-900">Rozdělení výnosů</h1>
                <p className="text-gray-600 mt-1">Analýza rozdělení tržeb podle různých kritérií</p>
              </div>
              <div className="flex items-center space-x-3">
                <select value={selectedPeriod} onChange={e => setSelectedPeriod(e.target.value)} className="border border-gray-300 rounded-lg px-3 py-2 text-sm">
                  <option value="week">Tento týden</option>
                  <option value="month">Tento měsíc</option>
                  <option value="quarter">Toto čtvrtletí</option>
                  <option value="year">Tento rok</option>
                </select>
                <button className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors">
                  <FontAwesomeIcon icon={faDownload} className="mr-2" />
                  Export
                </button>
              </div>
            </div>
          </section>

          {/* Summary Stats */}
          <section className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center">
                <div className="p-3 bg-green-50 rounded-lg">
                  <FontAwesomeIcon icon={faEuroSign} className="text-xl text-green-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Celkové výnosy</p>
                  <p className="text-2xl font-bold text-gray-900">{revenueDistribution.totalRevenue.toLocaleString()} Kč</p>
                  <div className="flex items-center text-sm mt-1">
                    <FontAwesomeIcon icon={faArrowUp} className="w-3 h-3 mr-1 text-green-600" />
                    <span className="text-green-600">+{comparisonData.growth.total}%</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center">
                <div className="p-3 bg-blue-50 rounded-lg">
                  <FontAwesomeIcon icon={faCut} className="text-xl text-blue-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Nejlepší služba</p>
                  <p className="text-lg font-bold text-gray-900">Střih a foukaná</p>
                  <p className="text-sm text-gray-600">50.2% z celku</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center">
                <div className="p-3 bg-purple-50 rounded-lg">
                  <FontAwesomeIcon icon={faUsers} className="text-xl text-purple-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Nejlepší zaměstnanec</p>
                  <p className="text-lg font-bold text-gray-900">Marie Krásná</p>
                  <p className="text-sm text-gray-600">29.2% z celku</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center">
                <div className="p-3 bg-orange-50 rounded-lg">
                  <FontAwesomeIcon icon={faCalendar} className="text-xl text-orange-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Nejlepší čas</p>
                  <p className="text-lg font-bold text-gray-900">16:00-18:00</p>
                  <p className="text-sm text-gray-600">24.4% z celku</p>
                </div>
              </div>
            </div>
          </section>

          {/* View Mode Selection */}
          <section className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Způsob zobrazení</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <button
                onClick={() => setViewMode('services')}
                className={`p-4 rounded-lg border-2 transition-colors text-left ${
                  viewMode === 'services' ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center space-x-3">
                  <FontAwesomeIcon icon={faCut} className="w-6 h-6 text-gray-600" />
                  <div>
                    <h4 className="font-medium text-gray-900">Podle služeb</h4>
                    <p className="text-sm text-gray-600">Rozdělení výnosů podle typů služeb</p>
                  </div>
                </div>
              </button>

              <button
                onClick={() => setViewMode('staff')}
                className={`p-4 rounded-lg border-2 transition-colors text-left ${
                  viewMode === 'staff' ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center space-x-3">
                  <FontAwesomeIcon icon={faUsers} className="w-6 h-6 text-gray-600" />
                  <div>
                    <h4 className="font-medium text-gray-900">Podle zaměstnanců</h4>
                    <p className="text-sm text-gray-600">Výnosy generované jednotlivými zaměstnanci</p>
                  </div>
                </div>
              </button>

              <button
                onClick={() => setViewMode('time')}
                className={`p-4 rounded-lg border-2 transition-colors text-left ${
                  viewMode === 'time' ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center space-x-3">
                  <FontAwesomeIcon icon={faCalendar} className="w-6 h-6 text-gray-600" />
                  <div>
                    <h4 className="font-medium text-gray-900">Podle času</h4>
                    <p className="text-sm text-gray-600">Rozdělení výnosů podle časových slotů</p>
                  </div>
                </div>
              </button>
            </div>
          </section>

          {/* Content */}
          {renderSelectedView()}

          {/* Quick Actions */}
          <section className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Rychlé akce</h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <button className="flex items-center justify-center p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-green-500 hover:bg-green-50 transition-colors">
                <FontAwesomeIcon icon={faChartPie} className="w-6 h-6 text-gray-400 mr-3" />
                <span className="text-gray-600">Vytvořit graf</span>
              </button>
              <button className="flex items-center justify-center p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors">
                <FontAwesomeIcon icon={faDownload} className="w-6 h-6 text-gray-400 mr-3" />
                <span className="text-gray-600">Export dat</span>
              </button>
              <button className="flex items-center justify-center p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-purple-500 hover:bg-purple-50 transition-colors">
                <FontAwesomeIcon icon={faEye} className="w-6 h-6 text-gray-400 mr-3" />
                <span className="text-gray-600">Detailní analýza</span>
              </button>
              <button className="flex items-center justify-center p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-orange-500 hover:bg-orange-50 transition-colors">
                <FontAwesomeIcon icon={faPercentage} className="w-6 h-6 text-gray-400 mr-3" />
                <span className="text-gray-600">Porovnat období</span>
              </button>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default RevenueDistributionPage;
